import React, { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils2";

/**
 * "Scan" button. Opens camera/file picker. Sends to /api/ocr/serial.
 * Calls onResult(text) with the extracted serial number.
 */
export default function ScanSerialButton({ onResult, className = "" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-pick same file
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/ocr/serial", fd);
      const text = (data?.text || "").trim();
      if (!text) {
        toast.error("Couldn't read a serial number — try a clearer photo");
      } else {
        toast.success(`Read: ${text}`);
        onResult?.(text);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className={`bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-orange-600 rounded-sm uppercase tracking-widest text-[10px] font-bold ${className}`}
        data-testid="scan-serial-btn"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Camera className="w-3.5 h-3.5 mr-1" />}
        {busy ? "Reading…" : "Scan"}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick}
      />
    </>
  );
}
